import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, GST_STATES, PERMISSION, can, inboxApi, useAuth, type BillingProfile } from '@wa/shared';

const EMPTY: BillingProfile = {
  legalName: '', gstin: null, addressLine1: null, addressLine2: null,
  city: null, stateCode: null, postalCode: null, billingEmail: null,
};

/**
 * What goes on the invoice.
 *
 * The state is the field that matters most and looks least important: it is the
 * place of supply, and it decides whether the invoice carries CGST and SGST or IGST.
 * Getting it wrong produces a perfectly ordinary-looking document the client's
 * accountant cannot use, so it says so on the field rather than in a help page.
 */
export function BillingDetailsPanel() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [draft, setDraft] = useState<BillingProfile>(EMPTY);

  const profile = useQuery({ queryKey: ['billing-profile'], queryFn: inboxApi.billingProfile });

  // Seeded once the server answers, never from an undefined query — initialising
  // empty and correcting later makes the form flash as though details were lost.
  useEffect(() => { if (profile.data) setDraft(profile.data); }, [profile.data]);

  const save = useMutation({
    mutationFn: () => inboxApi.saveBillingProfile(draft),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['billing-profile'] });
    },
    onError: (e) => { setSaved(false); setError(e instanceof ApiError ? e.message : 'Could not save those details.'); },
  });

  const editable = can(user, PERMISSION.settingsWrite);
  const set = (patch: Partial<BillingProfile>) => { setSaved(false); setDraft((d) => ({ ...d, ...patch })); };

  return (
    <div className="flex flex-col gap-4">
      {error && (
        <p role="alert" className="rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
          {error}
        </p>
      )}

      {saved && !save.isPending && (
        <p role="status" className="rounded border border-(--color-good) px-3 py-2 text-small text-(--color-good)">
          Saved. Invoices raised from now on carry these details; ones already issued keep the details they were raised with.
        </p>
      )}

      <section className="rounded-lg border border-(--color-line) bg-(--color-surface)">
        <h2 className="border-b border-(--color-line) px-3 py-2.5 text-body font-semibold sm:px-4">
          Invoice details
        </h2>

        <div className="grid gap-3 px-3 py-3 sm:px-4 @min-[30rem]:grid-cols-2">
          <Field label="Registered business name" className="@min-[30rem]:col-span-2">
            <input
              value={draft.legalName}
              disabled={!editable}
              onChange={(e) => set({ legalName: e.target.value })}
              placeholder="Acme Trading Private Limited"
              className={input}
            />
          </Field>

          <Field label="GSTIN" hint="15 characters. Leave blank if you are not registered.">
            <input
              value={draft.gstin ?? ''}
              disabled={!editable}
              onChange={(e) => set({ gstin: e.target.value.toUpperCase() || null })}
              placeholder="27AAAAA0000A1Z5"
              className={`${input} font-mono`}
            />
          </Field>

          <Field label="State" hint="Your place of supply. It decides CGST + SGST or IGST on every invoice.">
            <select
              value={draft.stateCode ?? ''}
              disabled={!editable}
              onChange={(e) => set({ stateCode: e.target.value || null })}
              className={input}
            >
              <option value="">Not set</option>
              {GST_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
            </select>
          </Field>

          <Field label="Address" className="@min-[30rem]:col-span-2">
            <input
              value={draft.addressLine1 ?? ''}
              disabled={!editable}
              onChange={(e) => set({ addressLine1: e.target.value || null })}
              placeholder="Unit 4, Sai Chambers"
              className={input}
            />
          </Field>

          <Field label="Area" className="@min-[30rem]:col-span-2">
            <input
              value={draft.addressLine2 ?? ''}
              disabled={!editable}
              onChange={(e) => set({ addressLine2: e.target.value || null })}
              placeholder="Shivaji Nagar"
              className={input}
            />
          </Field>

          <Field label="City">
            <input
              value={draft.city ?? ''}
              disabled={!editable}
              onChange={(e) => set({ city: e.target.value || null })}
              placeholder="Pune"
              className={input}
            />
          </Field>

          <Field label="PIN code">
            <input
              value={draft.postalCode ?? ''}
              disabled={!editable}
              onChange={(e) => set({ postalCode: e.target.value || null })}
              placeholder="411005"
              className={input}
            />
          </Field>

          <Field label="Billing email" hint="Where invoices are sent, if it differs from your account email."
                 className="@min-[30rem]:col-span-2">
            <input
              type="email"
              value={draft.billingEmail ?? ''}
              disabled={!editable}
              onChange={(e) => set({ billingEmail: e.target.value || null })}
              placeholder="accounts@example.com"
              className={input}
            />
          </Field>

          {editable && (
            <div className="@min-[30rem]:col-span-2">
              <button
                type="button"
                disabled={save.isPending || draft.legalName.trim().length === 0}
                onClick={() => save.mutate()}
                className="tap rounded bg-(--color-brand) px-3 py-1.5 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
              >
                {save.isPending ? 'Saving…' : 'Save details'}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

const input = 'w-full rounded border border-(--color-line) bg-(--color-surface) px-2 py-1.5 text-small disabled:opacity-60';

function Field({ label, hint, className, children }: {
  label: string; hint?: string; className?: string; children: React.ReactNode;
}) {
  return (
    <label className={['flex flex-col gap-1', className ?? ''].join(' ')}>
      <span className="text-micro font-medium uppercase tracking-wide text-(--color-faint)">{label}</span>
      {children}
      {hint && <span className="text-micro leading-snug text-(--color-muted)">{hint}</span>}
    </label>
  );
}
